import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { browser } from '../../global/browser';
import { compareNumbersAsStrings } from '../../global/comparators';
import type {
    UILayoutType,
    UILayoutTypes,
    UILayoutPageType,
} from './ui-layout.type';
import type { UILayoutHead } from './ui-layout-head';
import type { UILayoutContent } from './ui-layout-content';
import type { UILayoutSidebar } from './ui-layout-sidebar';
import styles from './ui-layout.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UILayout
 * @element ui-layout
 * @classdesc Represents a class for <code>ui-layout</code> element.
 * @property {("default" | "two-column" | "box")} [type] {@attr type} Type of the layout.
 *  {@desc default: standard layout without special positioning}
 *  {@desc two-column: has left and right columns}
 *  {@desc box: has vertically and horizontally centered layout}
 * @property {("functional")} [pageType] {@attr page-type} Page type of the layout.
 * @property {UILayoutHead} [head] Shortcut to UILayoutHead child element.
 * @property {UILayoutContent} [content] Shortcut to UILayoutContent child element.
 * @property {UILayoutSidebar} [sidebar] Shortcut to UILayoutSidebar child element.
 * @example
 * <ui-layout>
 *     <ui-layout-head>
 *         <h1>Page title</h1>
 *     </ui-layout-head>
 *     <ui-layout-content>
 *         <section class="frame">Content One</section>
 *     </ui-layout-content>
 *     <ui-layout-sidebar>
 *         <section class="frame">Sidebar One</section>
 *     </ui-layout-sidebar>
 * </ui-layout>
 */
@Component({ tag: 'ui-layout', styles })
export class UILayout extends UIElement {
    static readonly Type: UILayoutTypes = {
        Default: 'default',
        TwoColumn: 'two-column',
        Box: 'box',
    } as const;

    @Attribute(String) type?: UILayoutType;
    @Attribute(String) pageType?: UILayoutPageType;
    @Child('ui-layout-head') head?: UILayoutHead;
    @Child('ui-layout-content') content?: UILayoutContent;
    @Child('ui-layout-sidebar') sidebar?: UILayoutSidebar;

    private sidebarNodes: Array<HTMLElement> = [];
    private sortedSidebarNodes: Array<HTMLElement> = [];

    /**
     * Used to set mobile layout when type is two column.
     * The logic was created with JS. Doing it with CSS flexbox / grid can create a disconnect
     * between the DOM order and visual presentation of content, causing keyboard navigation to break,
     * which affects accessibility.
     */
    setMobileLayout(): void {
        if (this.type !== UILayout.Type.TwoColumn) {
            return;
        }

        this.sortedSidebarNodes?.forEach((sidebarNode: HTMLElement) => {
            const order = Number(sidebarNode.dataset.order);
            if (order) {
                this.content?.insertBefore(
                    sidebarNode,
                    this.content?.children[order - 1]
                );
            }
        });
    }

    /**
     * Used to set desktop layout when type is two column.
     * The logic was created with JS. Doing it with CSS flexbox / grid can create a disconnect
     * between the DOM order and visual presentation of content, causing keyboard navigation to break,
     * which affects accessibility.
     */
    setDesktopLayout(): void {
        if (this.type !== UILayout.Type.TwoColumn) {
            return;
        }

        this.sidebar?.append(...this.sidebarNodes);
    }

    /**
     * Render the layout by the type of screen. Used to handle two-column layout.
     * @param {boolean} isDesktop
     */
    renderByViewport(isDesktop: boolean): void {
        isDesktop ? this.setDesktopLayout() : this.setMobileLayout();
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        if (this.pageType && this.pageType === 'functional' && this.head) {
            this.head.setAttribute('alignment', 'left');
        }

        if (!this.sidebar) {
            return;
        }

        this.type = UILayout.Type.TwoColumn;
        this.sidebarNodes = [...this.sidebar.children] as Array<HTMLElement>;
        this.sortedSidebarNodes = [...this.sidebarNodes].sort((a, b) =>
            compareNumbersAsStrings(
                a.dataset.order ?? '',
                b.dataset.order ?? ''
            )
        );
        !browser.desktopMediaMatches() && this.renderByViewport(false);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        if (this.sidebarNodes?.length) {
            browser.desktopMedia.addListener((event) => {
                this.renderByViewport(event.matches);
            });
        }
    }
}
